import c from "classnames"
import { useMemo, useState } from "react"
import { useNavigate } from "react-router-dom"
import Card from "modules/Core/sub-modules/ui-kit/components/Card"
import Screen from "modules/Core/sub-modules/ui-kit/components/Screen"
import Text from "modules/Core/sub-modules/ui-kit/components/Text"
import PaymentCard from "modules/payments/components/PaymentCard"
import middleware from "modules/membership/middleware"
import Button from "modules/Core/sub-modules/ui-kit/components/Button"
import withPaymentsWrapper from "modules/payments/hocs/withPaymentsWrapper"
import Container from "modules/Core/sub-modules/ui-kit/components/Container"
import Drawer from "modules/Core/sub-modules/ui-kit/components/Drawer"
import { useIsOpen } from "modules/Core/hooks/useIsOpen"
import Title from "modules/Core/sub-modules/ui-kit/components/Title"
import { SUBSCRIPTION_TIERS } from "modules/membership/consts"
import styles from "./styles.module.scss";

const TIER_ORDER = [SUBSCRIPTION_TIERS.FREE, SUBSCRIPTION_TIERS.BASIC, SUBSCRIPTION_TIERS.PREMIUM];

const TIER_CARD_CONTENT = {
    [SUBSCRIPTION_TIERS.FREE]: {
        title: "Free tier",
        benefit: { emoji: "🌱", label: "Nurture your growth" },
        description: "Explore features and get started at your own pace—no commitment required.",
        inclusions: [
            "Access to three booths",
            "Explore features at your own pace",
        ],
        price: "0",
    },
    [SUBSCRIPTION_TIERS.BASIC]: {
        title: "Membership tier",
        benefit: { emoji: "✨", label: "Embrace the journey" },
        description: "Booths membership to support your growth and development.",
        inclusions: [
            "Unlimited booths",
            "Access to feature-requests",
        ],
        price: "5",
    },
    [SUBSCRIPTION_TIERS.PREMIUM]: {
        title: "Premium membership tier",
        benefit: { emoji: "🎯", label: "Stay committed" },
        description: "Everything in Basic, plus dedicated support and tools.",
        inclusions: [
            "Everything in Basic tier",
            "One-to-one coaching (30 min per month)",
            "Access to project-management booths",
        ],
        price: "18",
    },
}

const PaymentsSection = withPaymentsWrapper(({ clientSecret, options }) => (
    <Container className={c(styles.paymentsContainer, { [styles.open]: Boolean(clientSecret) })}>
        <Title> Subscribe as a member</Title>
        <PaymentCard className={styles.paymentsCard} options={options} clientSecret={clientSecret} />
    </Container>
))

const MembershipRegistration = () => {
    const [clientSecret, setClientSecret] = useState(null);
    const [tier, setTier] = useState(SUBSCRIPTION_TIERS.FREE);
    const [isLoading, setIsLoading] = useState(false);
    const { isOpen, open, close } = useIsOpen();
    const navigate = useNavigate();

    const fetchClientSecret = async () => {
        setIsLoading(true);
        try {
            const res = await middleware.ops.create({ tier });
            if (!res?.initMembership?.clientSecret) throw new Error("Failed to prepare payment");
            if (res.error) throw new Error(res.error);
            setClientSecret(res.initMembership.clientSecret);
            open();
        } finally {
            setIsLoading(false);
        }
    };


    const paymentOptions = useMemo(() => ({
        onSuccess: () => {
            close();
            navigate('/subscribe/success');
        }
    }), [close, navigate]);

    return (
        <Screen flex className={styles.container}>
            {/* Back Arrow */}
            <button className={styles.backButton} onClick={() => navigate(-1)} aria-label="Go back">
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <path d="M19 12H5M12 19l-7-7 7-7"/>
                </svg>
                <span>Back</span>
            </button>

            {/* Two Column Grid */}
            <div className={styles.gridContainer}>
                {/* Left Column - Main Content */}
                <div className={styles.leftColumn}>
                    {TIER_ORDER.map((tierOption) => (
                        <Card 
                            className={c(styles.card, { [styles.selected]: tier === tierOption })} 
                            key={tierOption} 
                            onClick={() => setTier(tierOption)}
                        >
                            <div className={styles.subscribeContent}>
                                <Title>{TIER_CARD_CONTENT[tierOption].title}</Title>
                                <Text className={styles.mainText}>{TIER_CARD_CONTENT[tierOption].description}</Text>
                                <ul className={styles.inclusionsList}>
                                    {TIER_CARD_CONTENT[tierOption].inclusions.map((item) => (
                                        <li key={item} className={styles.inclusionsItem}>
                                            <Text>{item}</Text>
                                        </li>
                                    ))}
                                </ul>
                                <div className={styles.tierBenefit}>
                                    <span className={styles.tierBenefitIcon}>{TIER_CARD_CONTENT[tierOption].benefit.emoji}</span>
                                    <Text className={styles.tierBenefitLabel}>{TIER_CARD_CONTENT[tierOption].benefit.label}</Text>
                                </div>
                            </div>
                        </Card>
                    ))}
                </div>

                {/* Right Column - Purchase Summary */}
                <div className={styles.rightColumn}>
                    <Card className={styles.summaryCard}>
                        <Container maxWidth center flex col>
                            <Title>Purchase Summary</Title>
                        </Container>
                        <div className={styles.summaryContent}>
                            <div className={styles.summaryItem}>
                                <Text bold>Membership Plan</Text>
                                <Text>{TIER_CARD_CONTENT[tier].title}</Text>
                            </div>
                            <div className={styles.summaryDivider} />
                            <div className={styles.summaryItem}>
                                <Text bold>Billing Cycle</Text>
                                <Text>
                                    {tier === SUBSCRIPTION_TIERS.FREE ? "N/A" : "Monthly (renews automatically)"}
                                </Text>
                            </div>
                            <div className={styles.summaryDivider} />
                            <div className={styles.totalSection}>
                                <Text bold className={styles.totalLabel}>Total Amount</Text>
                                <Text bold className={styles.totalAmount}>£{TIER_CARD_CONTENT[tier].price} / month</Text>
                            </div>
                        </div>
                    </Card>
                </div>
            </div>

            {/* Sticky footer when a paid tier is selected */}
            {tier && tier !== SUBSCRIPTION_TIERS.FREE && (
                <footer className={styles.tierFooter}>
                    <div className={styles.tierFooterContent}>
                        <div className={styles.tierFooterSummary}>
                            <Text bold className={styles.tierFooterPlan}>{TIER_CARD_CONTENT[tier].title}</Text>
                            <Text className={styles.tierFooterPrice}>£{TIER_CARD_CONTENT[tier].price} / month</Text>
                        </div>
                        <Button
                            className={styles.tierFooterButton}
                            onClick={fetchClientSecret}
                            loading={isLoading}
                            disabled={isLoading}
                        >
                            Subscribe
                        </Button>
                    </div>
                </footer>
            )}

            <Drawer className={styles.drawer} origin="right" isOpen={isOpen} close={close}>
                <PaymentsSection clientSecret={clientSecret} options={paymentOptions} tier={tier} />
            </Drawer>
        </Screen>
    )
}

export default MembershipRegistration;